import { tokens } from '@/theme'
import type { Lifecycle } from '@/domain/types'

interface Props {
  /** Stable stage code from `ref.InitiativeStage`. */
  stageCode: string
  /** Human label from the API (`stageName`). Falls back to the code. */
  stageName?: string
  /**
   * Derived lifecycle. Closed and blocked override the stage tone — where
   * an initiative *is* matters less than the fact that it stopped. The
   * stage label stays visible so the board still shows where it died.
   */
  lifecycle?: Lifecycle
  size?: 'sm' | 'md'
}

interface PillTone {
  fg: string
  bg: string
  border: string
}

/**
 * Six stage tones plus two lifecycle treatments.
 *
 * The ramp runs cool-to-warm along the funnel: the pre-commitment stages
 * are quiet, the active stages carry increasing weight, and Operate is the
 * green of something that landed.
 */
const STAGE_TONES: Record<string, PillTone> = {
  Idea: { fg: tokens.ink3, bg: tokens.paper3, border: tokens.line2 },
  Shaped: {
    fg: tokens.statusOrange,
    bg: tokens.statusOrangeWash,
    border: tokens.statusOrangeLine,
  },
  Experiment: { fg: tokens.statusBlue, bg: tokens.statusBlueWash, border: tokens.statusBlueLine },
  Pilot: { fg: tokens.statusTeal, bg: tokens.statusTealWash, border: tokens.statusTealLine },
  Scaling: { fg: tokens.statusCyanInk, bg: tokens.statusCyanWash, border: tokens.statusCyanLine },
  Operate: { fg: tokens.statusGreen, bg: tokens.statusGreenWash, border: tokens.statusGreenLine },
}

const NEUTRAL_TONE: PillTone = { fg: tokens.ink3, bg: tokens.paper3, border: tokens.line2 }

const BLOCKED_TONE: PillTone = {
  fg: tokens.statusAmber,
  bg: tokens.statusAmberWash,
  border: tokens.statusAmberLine,
}

const CLOSED_TONE: PillTone = {
  fg: tokens.statusRed,
  bg: tokens.statusRedWash,
  border: tokens.statusRedLine,
}

/**
 * Stage badge for an initiative. Unknown codes (a stage added to the
 * picklist ahead of a SPA release) render neutral so the surface degrades
 * rather than throwing.
 *
 * Replaces `StatusPill`, retired with `ref.InitiativeStatus`.
 */
export function StagePill({ stageCode, stageName, lifecycle = 'active', size = 'sm' }: Props) {
  const tone =
    lifecycle === 'closed'
      ? CLOSED_TONE
      : lifecycle === 'blocked'
        ? BLOCKED_TONE
        : (STAGE_TONES[stageCode] ?? NEUTRAL_TONE)

  const padding = size === 'sm' ? '3px 9px' : '5px 12px'
  const fontSize = size === 'sm' ? 10.5 : 12
  const label = stageName ?? stageCode

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding,
        fontSize,
        fontFamily: tokens.fontDisplay,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: tone.fg,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: tokens.radiusSm,
        whiteSpace: 'nowrap',
        // Closed work is history. Strike the label rather than hiding the
        // row — the proposal's whole argument for keeping killed ideas on
        // the board is that the graveyard is half the value.
        textDecoration: lifecycle === 'closed' ? 'line-through' : 'none',
      }}
    >
      {label}
      {lifecycle !== 'active' && (
        <span style={{ fontSize: fontSize - 1.5, opacity: 0.85, textDecoration: 'none' }}>
          · {lifecycle === 'closed' ? 'closed' : 'blocked'}
        </span>
      )}
    </span>
  )
}
